import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { CaretDown } from '@phosphor-icons/react';
import Popover from '../ui/Popover';
import Icon from '../ui/Icon';
import type { ProfilePublicHighlightsArticle } from '../../types/api';

interface PublicHighlightsMenuProps {
  userId: string;
  articles: ProfilePublicHighlightsArticle[];
  selectedBookmarkId?: string;
}

/** Dropdown of articles whose notes were explicitly shared by this profile. */
function PublicHighlightsMenu({
  userId,
  articles,
  selectedBookmarkId,
}: PublicHighlightsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (articles.length === 0) {
    return null;
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      role="menu"
      triggerClassName="shrink-0"
      content={
        <div className="max-w-80 min-w-64">
          {articles.map((article) => (
            <Link
              key={article.id}
              to="/p/$id/highlights/$bookmarkId"
              params={{ id: userId, bookmarkId: article.id }}
              onClick={() => setIsOpen(false)}
              role="menuitemradio"
              aria-checked={selectedBookmarkId === article.id}
              className={
                selectedBookmarkId === article.id
                  ? 'menu-item active h-auto! min-h-12 py-2'
                  : 'menu-item h-auto! min-h-12 py-2'
              }
            >
              <span className="flex min-w-0 flex-col">
                <span className="text-copy-primary line-clamp-2 leading-5 font-medium">
                  {article.title}
                </span>
                <span className="text-copy-secondary truncate text-xs leading-4">
                  {article.site_name}
                </span>
              </span>
            </Link>
          ))}
        </div>
      }
    >
      <button
        type="button"
        className="text-copy-secondary hover:text-copy-primary flex items-center gap-1.5 text-sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={t`Notes`}
      >
        <Icon name="note" size="sm" />
        <span aria-hidden="true">{articles.length}</span>
        <span className="sr-only">
          <Plural value={articles.length} one="# Note" other="# Notes" />
        </span>
        <CaretDown aria-hidden="true" size={12} weight="light" />
      </button>
    </Popover>
  );
}

export default PublicHighlightsMenu;
